import { apiGet, apiPost } from "@/services/http";
import { endpoints } from "./endpoints";
import type { ApiEnvelope, ApiErrorBody } from "@/types/api";
import { ApiError } from "@/services/http/errors";

export type NoteCustomerDto = {
  id: number;
  number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

export type NoteDto = {
  id: number;
  content: string;
  organization?: number | null;
  username?: number | null;
  customer?: NoteCustomerDto | number | null;
  session?: number | null;
  communication_channel?: number | null;
  created_at?: string;
  updated_at?: string | null;
};

type NotesListResponse =
  | (ApiEnvelope<NoteDto[]> & { count?: number })
  | {
      count?: number;
      next?: string | null;
      previous?: string | null;
      data: NoteDto[];
      message?: string;
      success?: boolean;
    };

export async function fetchNotesByNumber(number: string) {
  const digits = number.replace(/\D/g, "") || number.trim();
  const query = new URLSearchParams();
  if (digits) query.set("customer__number", digits);
  query.set("size", "100");
  query.set("ordering", "-created_at");
  const url = `${endpoints.notesList}?${query.toString()}`;

  const response = await apiGet<NotesListResponse>(url);
  const rows = Array.isArray(response?.data) ? response.data : [];
  if (!Array.isArray(response?.data)) {
    throw new ApiError(0, response, "Could not load notes.");
  }
  return [...rows].sort((a, b) => {
    const aTime = Date.parse(a.created_at || "") || a.id;
    const bTime = Date.parse(b.created_at || "") || b.id;
    return bTime - aTime;
  });
}

export type CreateNotePayload = {
  content: string;
  number: string;
};

export async function createNote(payload: CreateNotePayload) {
  const response = await apiPost<
    ApiEnvelope<NoteDto> & ApiErrorBody,
    CreateNotePayload
  >(endpoints.notesCreate, {
    content: payload.content,
    number: payload.number.replace(/\D/g, "") || payload.number.trim(),
  });

  if (!response?.data) {
    throw new ApiError(
      0,
      response,
      response?.error?.message || response?.message || "Could not save note.",
    );
  }

  return response.data;
}
